/**
 * Responsive Animation Hook
 * Adapts animations based on screen size, device capabilities, and user preferences
 */

import { useState, useEffect, useMemo } from 'react';
import { useReducedMotion, useAnimationConfig } from './context';
import type { MotionProps, Variants } from 'framer-motion';

interface BreakpointConfig {
  mobile: number;
  tablet: number;
  desktop: number;
  wide: number;
}

interface DeviceCapabilities {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWide: boolean;
  isTouchDevice: boolean;
  isLowEndDevice: boolean;
  connectionSpeed: 'slow' | 'medium' | 'fast';
}

interface ResponsiveAnimationOptions {
  breakpoints?: Partial<BreakpointConfig>;
  mobileAnimations?: MotionProps;
  tabletAnimations?: MotionProps;
  desktopAnimations?: MotionProps;
  wideAnimations?: MotionProps;
  touchOptimized?: boolean;
  reduceOnLowEnd?: boolean;
}

const defaultBreakpoints: BreakpointConfig = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440,
  wide: 1920
};

export const useResponsiveAnimation = (
  baseAnimations: MotionProps,
  options: ResponsiveAnimationOptions = {}
) => {
  const {
    breakpoints = {},
    mobileAnimations = {},
    tabletAnimations = {},
    desktopAnimations = {},
    wideAnimations = {},
    touchOptimized = true,
    reduceOnLowEnd = true
  } = options;

  const isReducedMotion = useReducedMotion();
  const config = useAnimationConfig();
  
  const finalBreakpoints = { ...defaultBreakpoints, ...breakpoints };
  
  const [deviceCapabilities, setDeviceCapabilities] = useState<DeviceCapabilities>({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isWide: false,
    isTouchDevice: false,
    isLowEndDevice: false,
    connectionSpeed: 'medium'
  });

  // Detect device capabilities
  useEffect(() => {
    const updateDeviceCapabilities = () => {
      const width = window.innerWidth;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Detect device performance level
      const cores = navigator.hardwareConcurrency || 4;
      const memory = (navigator as any).deviceMemory || 4;
      const isLowEndDevice = cores < 4 || memory < 4;
      
      // Detect connection speed
      const connection = (navigator as any).connection;
      let connectionSpeed: 'slow' | 'medium' | 'fast' = 'medium';
      
      if (connection) {
        const effectiveType = connection.effectiveType;
        if (effectiveType === '2g' || effectiveType === 'slow-2g') {
          connectionSpeed = 'slow';
        } else if (effectiveType === '4g') {
          connectionSpeed = 'fast';
        }
      }

      setDeviceCapabilities({
        isMobile: width < finalBreakpoints.mobile,
        isTablet: width >= finalBreakpoints.mobile && width < finalBreakpoints.tablet,
        isDesktop: width >= finalBreakpoints.tablet && width < finalBreakpoints.desktop,
        isWide: width >= finalBreakpoints.desktop,
        isTouchDevice,
        isLowEndDevice,
        connectionSpeed
      });
    };

    updateDeviceCapabilities();
    
    const handleResize = () => {
      updateDeviceCapabilities();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [finalBreakpoints]);

  // Generate responsive animations
  const responsiveAnimations = useMemo(() => {
    let animations = { ...baseAnimations };

    // Apply device-specific animations
    if (deviceCapabilities.isMobile) {
      animations = { ...animations, ...mobileAnimations };
    } else if (deviceCapabilities.isTablet) {
      animations = { ...animations, ...tabletAnimations };
    } else if (deviceCapabilities.isDesktop) {
      animations = { ...animations, ...desktopAnimations };
    } else if (deviceCapabilities.isWide) {
      animations = { ...animations, ...wideAnimations };
    }

    // Touch optimizations
    if (touchOptimized && deviceCapabilities.isTouchDevice) {
      animations = {
        ...animations,
        // Increase touch targets
        whileTap: animations.whileTap || { scale: 0.95 },
        // Remove hover effects on touch devices
        whileHover: undefined,
        // Optimize transitions for touch
        transition: {
          ...animations.transition,
          type: 'spring',
          stiffness: 400,
          damping: 30
        }
      };
    }

    // Low-end device optimizations
    if (reduceOnLowEnd && deviceCapabilities.isLowEndDevice) {
      animations = {
        ...animations,
        // Simplify animations
        transition: {
          ...animations.transition,
          duration: (animations.transition?.duration || config.customTimings.normal) * 0.7,
          ease: 'easeOut'
        },
        // Remove complex transforms
        animate: typeof animations.animate === 'object' ? {
          ...animations.animate,
          rotateX: undefined,
          rotateY: undefined,
          rotateZ: undefined,
          skewX: undefined,
          skewY: undefined
        } : animations.animate
      };
    }

    // Slow connection optimizations
    if (deviceCapabilities.connectionSpeed === 'slow') {
      animations = {
        ...animations,
        // Reduce animation duration
        transition: {
          ...animations.transition,
          duration: config.customTimings.fast
        }
      };
    }

    // Reduced motion fallback
    if (isReducedMotion) {
      animations = {
        ...animations,
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: config.customTimings.fast },
        whileHover: undefined,
        whileTap: undefined,
        whileFocus: { opacity: 1 }
      };
    }

    return animations;
  }, [
    baseAnimations,
    deviceCapabilities,
    mobileAnimations,
    tabletAnimations,
    desktopAnimations,
    wideAnimations,
    touchOptimized,
    reduceOnLowEnd,
    isReducedMotion,
    config.customTimings
  ]);

  return {
    animations: responsiveAnimations,
    deviceCapabilities,
    breakpoints: finalBreakpoints
  };
};

/**
 * Hook for creating responsive animation variants
 */
export const useResponsiveVariants = (
  baseVariants: Variants,
  responsiveVariants: {
    mobile?: Variants;
    tablet?: Variants;
    desktop?: Variants;
    wide?: Variants;
  } = {}
): Variants => {
  const { deviceCapabilities } = useResponsiveAnimation({});
  const isReducedMotion = useReducedMotion();

  return useMemo(() => {
    let variants = { ...baseVariants };

    // Apply device-specific variants
    if (deviceCapabilities.isMobile && responsiveVariants.mobile) {
      variants = { ...variants, ...responsiveVariants.mobile };
    } else if (deviceCapabilities.isTablet && responsiveVariants.tablet) {
      variants = { ...variants, ...responsiveVariants.tablet };
    } else if (deviceCapabilities.isDesktop && responsiveVariants.desktop) {
      variants = { ...variants, ...responsiveVariants.desktop };
    } else if (deviceCapabilities.isWide && responsiveVariants.wide) {
      variants = { ...variants, ...responsiveVariants.wide };
    }

    // Reduced motion fallback
    if (isReducedMotion) {
      const reducedVariants: Variants = {};
      Object.keys(variants).forEach(key => {
        reducedVariants[key] = {
          opacity: key === 'hidden' ? 0 : 1,
          transition: { duration: 0.2 }
        };
      });
      return reducedVariants;
    }

    return variants;
  }, [baseVariants, responsiveVariants, deviceCapabilities, isReducedMotion]);
};

/**
 * Hook for responsive touch gestures
 */
export const useResponsiveGestures = () => {
  const { deviceCapabilities } = useResponsiveAnimation({});
  
  return useMemo(() => {
    if (!deviceCapabilities.isTouchDevice) {
      return {};
    }

    return {
      drag: true,
      dragConstraints: { left: 0, right: 0, top: 0, bottom: 0 },
      dragElastic: 0.1,
      whileDrag: { scale: 1.05 },
      onDragEnd: (event: any, info: any) => {
        // Handle swipe gestures
        const threshold = 50;
        if (Math.abs(info.offset.x) > threshold) {
          // Horizontal swipe detected
          console.log('Horizontal swipe:', info.offset.x > 0 ? 'right' : 'left');
        }
        if (Math.abs(info.offset.y) > threshold) {
          // Vertical swipe detected
          console.log('Vertical swipe:', info.offset.y > 0 ? 'down' : 'up');
        }
      }
    };
  }, [deviceCapabilities.isTouchDevice]);
};

export default useResponsiveAnimation;